/* ====================================================================
   IMPORTS
   ==================================================================== */

import isBreakpoint from "./_isBreakpoint";
import purgeProperties from "./_purgeProperties";

/* ====================================================================
   HELPERS —> CREATE BEHAVIOUR
   ==================================================================== */

// eslint-disable-next-line require-jsdoc
function Behaviour(node, config = {}) {
  if (!node || !(node instanceof Element)) {
    throw new Error("The node parameter is required");
  }

  this.node = node;
  this.options = Object.assign({}, config.options || {});

  this.__subBehaviours = [];
  this.__isEnabled = false;
  this.__children = config.children;

  /**
   * Auto-bind all custom methods to `this`
   */

  this.customMethodNames.forEach(methodName => {
    this[methodName] = this[methodName].bind(this);
  });

  return this;
}

Behaviour.prototype = Object.freeze({
  init() {
    /**
     * Get options from the behaviour node
     */

    const regex = new RegExp("^data-" + this.name + "-(.*)", "i");

    for (let i = 0; i < this.node.attributes.length; i++) {
      const attr = this.node.attributes[i];
      const matches = regex.exec(attr.nodeName);

      if (matches != null && matches.length >= 2) {
        if (this.options[matches[1]]) {
          console.warn(
            `Ignoring ${
              matches[1]
            } option, as it already exists on the ${name} behaviour. Please choose another name.`
          );
        }

        this.options[matches[1]] = attr.value;
      }
    }

    /**
     * Call the init function and add the subsequent lifecycle event listeners
     */

    if (this.lifecycle.init != null) {
      this.lifecycle.init.call(this);
    }

    if (this.lifecycle.resized != null) {
      this.__resizedBind = this.__resized.bind(this);
      window.addEventListener("resized", this.__resizedBind);
    }

    if (this.lifecycle.mediaQueryUpdated != null || this.options.media) {
      this.__mediaQueryUpdatedBind = this.__mediaQueryUpdated.bind(this);
      window.addEventListener(
        "mediaQueryUpdated",
        this.__mediaQueryUpdatedBind
      );
    }

    if (this.options.media) {
      this.__toggleEnabled();
    } else {
      this.enable();
    }
  },
  destroy() {
    if (this.__isEnabled === true) {
      this.disable();
    }

    /**
     * Call the destroy function and remove the subsequent lifecycle event listeners
     */

    if (this.lifecycle.destroy != null) {
      this.lifecycle.destroy.call(this);
    }

    this.__subBehaviours.forEach(sub => {
      sub.destroy();
    });

    if (this.lifecycle.resized != null) {
      window.removeEventListener("resized", this.__resizedBind);
    }

    if (this.lifecycle.mediaQueryUpdated != null || this.options.media) {
      window.removeEventListener(
        "mediaQueryUpdated",
        this.__mediaQueryUpdatedBind
      );
    }

    purgeProperties(this);
  },
  getChild(childName, context, multi = false) {
    if (context == null) {
      context = this.node;
    }

    if (this.__children != null && this.__children[childName] != null) {
      return this.__children[childName];
    }

    return context[multi ? "querySelectorAll" : "querySelector"](
      "[data-" + this.name.toLowerCase() + "-" + childName.toLowerCase() + "]"
    );
  },
  getChildren(childName, context) {
    return this.getChild(childName, context, true);
  },
  isEnabled() {
    return this.__isEnabled;
  },
  enable() {
    this.__isEnabled = true;

    if (this.lifecycle.enabled != null) {
      this.lifecycle.enabled.call(this);
    }
  },
  disable() {
    this.__isEnabled = false;

    if (this.lifecycle.disabled != null) {
      this.lifecycle.disabled.call(this);
    }
  },
  addSubBehaviour(Behaviour, node, config = {}) {
    const subBehaviour = new Behaviour(node, config);

    subBehaviour.behaviourName = this.name;
    subBehaviour.init();

    this.__subBehaviours.push(subBehaviour);
    return subBehaviour;
  },
  __toggleEnabled() {
    const isValidMQ = isBreakpoint(this.options.media);

    if (isValidMQ && !this.__isEnabled) {
      this.enable();
    } else if (!isValidMQ && this.__isEnabled) {
      this.disable();
    }
  },
  __mediaQueryUpdated() {
    if (this.lifecycle.mediaQueryUpdated != null) {
      this.lifecycle.mediaQueryUpdated.call(this);
    }

    if (this.options.media) {
      this.__toggleEnabled();
    }
  },
  __resized() {
    if (this.lifecycle.resized != null) {
      this.lifecycle.resized.call(this);
    }
  }
});

const createBehaviour = (name, def, lifecycle = {}) => {
  const fn = function(...args) {
    // eslint-disable-next-line no-invalid-this
    Behaviour.apply(this, args);
  };

  const customMethodNames = [];

  const customProperties = {
    name: {
      get() {
        return this.behaviourName;
      }
    },
    behaviourName: {
      value: name,
      writable: true
    },
    lifecycle: {
      value: lifecycle
    },
    customMethodNames: {
      value: customMethodNames
    }
  };

  /**
   * Expose the definition properties as `this[methodName]`
   */

  const defKeys = Object.keys(def);
  defKeys.forEach(key => {
    customMethodNames.push(key);

    customProperties[key] = {
      value: def[key],
      writable: true
    };
  });

  fn.prototype = Object.create(Behaviour.prototype, customProperties);

  return fn;
};

export default createBehaviour;
